import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableRow, TableCell, TableHead, TableHeader } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Loader2, ArrowRight, DollarSign, Eye, Plug } from 'lucide-react';
import { ShinyButton } from '../magicui/shiny-button';
import Assessments from '../assessments/Assessments';
import Reports from '../reports/Reports';
import { VisitorJourney } from '../journey';
import SalesforceIcon from '../icons/salesforce_logo.png';
import HubspotIcon from '../icons/hubspot_logo.webp';
import GoogleAdsIcon from '../icons/google_ads_logo.png';
import AttioIcon from '../icons/attio_logo.png';
import { formatDate } from '../utils/dateFormatter';
import useApi from '../api';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import VisitorIdentity from '../VisitorIdentity';
import ContactObjectives from './ContactObjectives';
import ProactiveMessages from './ProactiveMessages';
import SectionHeader from '@/components/ui/SectionHeader';

interface Lead {
  id: string;
  organization_id?: string;
  first_name?: string;
  last_name?: string;
  company?: string;
  email?: string;
  email_verified?: boolean | null;
  identity_verified_at?: string | null;
  job_title?: string;
  revenue?: number | null;
  source?: string;
  external_id?: string;
  integrations?: {
    salesforce?: string;
    salesforce_error?: string;
    salesforce_contact?: string;
    salesforce_contact_error?: string;
    salesforce_account?: string;
    salesforce_account_error?: string;
    hubspot?: string;
    hubspot_error?: string;
    attio?: string;
    attio_error?: string;
    attio_company?: string;
    attio_company_error?: string;
    google_ads_conversion_status?: string;
    google_ads_conversion_error?: string;
    google_ads_error?: string;
    hubspot_deals?: any[];
    salesforce_opportunities?: any[];
  };
  created_at?: string;
  updated_at?: string;
  attributes?: any;
}

interface Deal {
  id: string;
  name: string;
  amount: number;
  stage: string;
  close_date: string;
  pipeline?: string;
  create_date: string;
  last_modified: string;
  source: 'hubspot' | 'salesforce';
}

interface LeadSheetProps {
  lead: Lead | null;
  onClose: () => void;
  onLeadUpdate?: (updatedLead: Lead) => void;
}

const HUBSPOT_GENERIC_SYNC_ERROR = 'HubSpot sync failed due to an unexpected internal error. Please try again or contact support if this continues.';
const INTERNAL_HUBSPOT_ERROR_PATTERNS = [/greenlet_spawn/i, /await_only/i, /sqlalche\.me/i, /Error in sync_hubspot/i];

const getCustomerFacingHubspotError = (error?: string | null) => {
  if (!error) return error;
  return INTERNAL_HUBSPOT_ERROR_PATTERNS.some((pattern) => pattern.test(error))
    ? HUBSPOT_GENERIC_SYNC_ERROR
    : error;
};

const DealsTable: React.FC<{ lead: Lead }> = ({ lead }) => {
  const hasHubspotDeals = lead.integrations?.hubspot_deals && lead.integrations.hubspot_deals.length > 0;
  const hasSalesforceOpportunities = lead.integrations?.salesforce_opportunities && lead.integrations.salesforce_opportunities.length > 0;

  if (!hasHubspotDeals && !hasSalesforceOpportunities) {
    return null;
  }

  const allDeals: Deal[] = [];

  if (hasHubspotDeals && lead.integrations?.hubspot_deals) {
    lead.integrations.hubspot_deals.forEach((deal: any) => {
      allDeals.push({
        ...deal,
        source: 'hubspot'
      });
    });
  }

  if (hasSalesforceOpportunities && lead.integrations?.salesforce_opportunities) {
    lead.integrations.salesforce_opportunities.forEach((opp: any) => {
      allDeals.push({
        ...opp,
        source: 'salesforce'
      });
    });
  }

  const sortedDeals = [...allDeals].sort((a, b) => b.amount - a.amount);

  return (
    <>
      <SectionHeader title="Deals" icon={DollarSign} className="mt-4" />
      <div className="mt-2 border rounded-lg overflow-hidden">
        <Table>
          <thead>
            <TableRow>
              <th className="px-4 py-2 text-left text-sm font-medium">Source</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Amount</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Stage</th>
              <th className="px-4 py-2 text-left text-sm font-medium">Close Date</th>
            </TableRow>
          </thead>
          <TableBody>
            {sortedDeals.length ? (
              sortedDeals.map((deal, index) => (
                <TableRow key={`${deal.source}-${deal.id || index}`}>
                  <TableCell>
                    {deal.source === 'hubspot' ? (
                      <img src={HubspotIcon} alt="HubSpot" className="w-5 h-5 object-contain" />
                    ) : (
                      <img src={SalesforceIcon} alt="Salesforce" className="w-5 h-5 object-contain" />
                    )}
                  </TableCell>
                  <TableCell>{deal.name || 'Unnamed Deal'}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center text-sm font-medium">
                      <DollarSign className="mr-1 h-3 w-3" />
                      {new Intl.NumberFormat('en-US', {
                        style: 'decimal',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(deal.amount || 0)}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{deal.stage || 'N/A'}</TableCell>
                  <TableCell className="text-sm">{deal.close_date ? formatDate(deal.close_date) : 'N/A'}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center">No deals found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
};

export const LeadSheet: React.FC<LeadSheetProps> = ({ lead, onClose, onLeadUpdate }) => {
  const navigate = useNavigate();
  const { get, post } = useApi();
  const [resolvedLead, setResolvedLead] = useState<Lead | null>(lead);
  const [loadingLead, setLoadingLead] = useState(false);

  const [hubspotDialogOpen, setHubspotDialogOpen] = useState(false);
  const [resyncingHubspot, setResyncingHubspot] = useState(false);
  const [salesforceDialogOpen, setSalesforceDialogOpen] = useState(false);
  const [resyncingSalesforce, setResyncingSalesforce] = useState(false);
  const [attioDialogOpen, setAttioDialogOpen] = useState(false);
  const [resyncingAttio, setResyncingAttio] = useState(false);
  const [googleAdsDialogOpen, setGoogleAdsDialogOpen] = useState(false);
  const [resyncingGoogleAds, setResyncingGoogleAds] = useState(false);
  const [activeSalesforceError, setActiveSalesforceError] = useState<string | null>(null);
  const [activeHubspotError, setActiveHubspotError] = useState<string | null>(null);
  const [activeAttioError, setActiveAttioError] = useState<string | null>(null);

  useEffect(() => {
    setResolvedLead(lead);
  }, [lead]);

  useEffect(() => {
    const fetchLead = async () => {
      if (!lead?.id) {
        setResolvedLead(lead);
        return;
      }

      setLoadingLead(true);
      try {
        const response = await get(`/leads/${lead.id}`);
        setResolvedLead(response.data || lead);
      } catch (error) {
        console.error('Error fetching lead for sheet:', error);
        setResolvedLead(lead);
      } finally {
        setLoadingLead(false);
      }
    };

    void fetchLead();
  }, [get, lead]);

  const activeLead = resolvedLead || lead;

  const crmSyncRows = React.useMemo(() => {
    if (!activeLead?.integrations) return [];

    type CrmKey = 'salesforce' | 'hubspot' | 'attio';
    type CrmRow = {
      object: string;
      crm: CrmKey;
      url?: string;
      error?: string;
      icon: string;
    };

    const i = activeLead.integrations;
    const rows: CrmRow[] = [];

    const pushRow = (row: CrmRow) => {
      if (row.url || row.error) rows.push(row);
    };

    pushRow({ object: 'Lead', crm: 'salesforce', url: i.salesforce, error: i.salesforce_error, icon: SalesforceIcon });
    pushRow({ object: 'Contact', crm: 'salesforce', url: i.salesforce_contact, error: i.salesforce_contact_error, icon: SalesforceIcon });
    pushRow({ object: 'Account', crm: 'salesforce', url: i.salesforce_account, error: i.salesforce_account_error, icon: SalesforceIcon });
    pushRow({ object: 'Contact', crm: 'hubspot', url: i.hubspot, error: getCustomerFacingHubspotError(i.hubspot_error) ?? undefined, icon: HubspotIcon });
    pushRow({ object: 'Person', crm: 'attio', url: i.attio, error: i.attio_error, icon: AttioIcon });
    pushRow({ object: 'Company', crm: 'attio', url: i.attio_company, error: i.attio_company_error, icon: AttioIcon });

    return rows;
  }, [activeLead]);

  const handleResyncHubspot = async (leadId: string) => {
    if (resyncingHubspot) return;

    try {
      setResyncingHubspot(true);
      const response = await post(`/leads/${leadId}/resync-hubspot`, {});

      if (response.data.status === 'success') {
        toast.success('Successfully resynced to HubSpot', toastConfig);

        if (activeLead && activeLead.id === leadId && onLeadUpdate) {
          const updatedLead = { ...activeLead };
          updatedLead.integrations = {
            ...updatedLead.integrations,
            hubspot: response.data.contact_url,
          };
          if (updatedLead.integrations?.hubspot_error) {
            delete updatedLead.integrations.hubspot_error;
          }
          onLeadUpdate(updatedLead);
          setResolvedLead(updatedLead);
        }

        setHubspotDialogOpen(false);
      } else {
        toast.error(`Failed to resync`, toastConfig);
      }
    } catch (error: any) {
      console.error('Error resyncing to HubSpot:', error);
      toast.error(`Error resyncing to HubSpot: ${error.message || 'Unknown error'}`, toastConfig);
    } finally {
      setResyncingHubspot(false);
    }
  };

  const handleResyncSalesforce = async (leadId: string) => {
    if (resyncingSalesforce) return;

    try {
      setResyncingSalesforce(true);
      const response = await post(`/leads/${leadId}/resync-salesforce`, {});

      if (response.data.status === 'success') {
        toast.success('Successfully resynced to Salesforce', toastConfig);

        if (activeLead && activeLead.id === leadId && onLeadUpdate) {
          const updatedLead = { ...activeLead };
          updatedLead.integrations = {
            ...updatedLead.integrations,
            salesforce: response.data.lead_url,
            ...(response.data.contact_url ? { salesforce_contact: response.data.contact_url } : {}),
            ...(response.data.account_url ? { salesforce_account: response.data.account_url } : {}),
          };
          if (updatedLead.integrations?.salesforce_error) {
            delete updatedLead.integrations.salesforce_error;
          }
          if (response.data.contact_url && updatedLead.integrations?.salesforce_contact_error) {
            delete updatedLead.integrations.salesforce_contact_error;
          }
          if (response.data.account_url && updatedLead.integrations?.salesforce_account_error) {
            delete updatedLead.integrations.salesforce_account_error;
          }
          onLeadUpdate(updatedLead);
          setResolvedLead(updatedLead);
        }

        setSalesforceDialogOpen(false);
      } else {
        toast.error(`Failed to resync to Salesforce`, toastConfig);
      }
    } catch (error: any) {
      console.error('Error resyncing to Salesforce:', error);
      toast.error(`Error resyncing to Salesforce: ${error.message || 'Unknown error'}`, toastConfig);
    } finally {
      setResyncingSalesforce(false);
    }
  };

  const handleResyncGoogleAds = async (leadId: string) => {
    if (resyncingGoogleAds) return;

    try {
      setResyncingGoogleAds(true);
      const response = await post(`/leads/${leadId}/resync-google-ads`, {});

      if (response.data.status === 'success') {
        toast.success('Successfully resynced to Google Ads', toastConfig);

        if (activeLead && activeLead.id === leadId && onLeadUpdate) {
          const updatedLead = { ...activeLead };
          updatedLead.integrations = {
            ...updatedLead.integrations,
            google_ads_conversion_status: "success",
          };
          if (updatedLead.integrations?.google_ads_error) {
            delete updatedLead.integrations.google_ads_error;
          }
          onLeadUpdate(updatedLead);
          setResolvedLead(updatedLead);
        }

        setGoogleAdsDialogOpen(false);
      } else if (response.data.status === 'partial_success') {
        toast.success('Partially resynced to Google Ads', toastConfig);

        if (activeLead && activeLead.id === leadId && onLeadUpdate) {
          const updatedLead = { ...activeLead };
          updatedLead.integrations = {
            ...updatedLead.integrations,
            google_ads_conversion_status: "partial_success",
          };
          if (updatedLead.integrations?.google_ads_error) {
            delete updatedLead.integrations.google_ads_error;
          }
          onLeadUpdate(updatedLead);
          setResolvedLead(updatedLead);
        }

        setGoogleAdsDialogOpen(false);
      } else {
        toast.error(`Failed to resync to Google Ads`, toastConfig);
      }
    } catch (error: any) {
      console.error('Error resyncing to Google Ads:', error);
      toast.error(`Error resyncing to Google Ads: ${error.message || 'Unknown error'}`, toastConfig);
    } finally {
      setResyncingGoogleAds(false);
    }
  };

  const handleResyncAttio = async (leadId: string) => {
    if (resyncingAttio) return;

    try {
      setResyncingAttio(true);
      const response = await post(`/leads/${leadId}/resync-attio`, {});

      if (response.data.status === 'success') {
        toast.success('Successfully resynced to Attio', toastConfig);

        if (activeLead && activeLead.id === leadId && onLeadUpdate) {
          const updatedLead = { ...activeLead };
          updatedLead.integrations = {
            ...updatedLead.integrations,
            ...(response.data.record_url ? { attio: response.data.record_url } : {}),
            ...(response.data.company_url ? { attio_company: response.data.company_url } : {}),
          };
          if (updatedLead.integrations?.attio_error) {
            delete updatedLead.integrations.attio_error;
          }
          if (response.data.company_url && updatedLead.integrations?.attio_company_error) {
            delete updatedLead.integrations.attio_company_error;
          }
          onLeadUpdate(updatedLead);
          setResolvedLead(updatedLead);
        }

        setAttioDialogOpen(false);
      } else {
        toast.error('Failed to resync to Attio', toastConfig);
      }
    } catch (error: any) {
      console.error('Error resyncing to Attio:', error);
      toast.error(`Error resyncing to Attio: ${error.message || 'Unknown error'}`, toastConfig);
    } finally {
      setResyncingAttio(false);
    }
  };

  return (
    <Sheet open={lead !== null} onOpenChange={onClose}>
      <SheetContent className="min-w-[50%] grow overflow-y-auto overflow-x-hidden" aria-describedby={undefined}>
        <VisuallyHidden>
          <SheetTitle>Lead Details</SheetTitle>
        </VisuallyHidden>
        {activeLead && (
          <div className="space-y-4">
            <div className="w-full space-y-4">
              <VisitorIdentity leadId={activeLead.id} lead={activeLead} mode="full" showLeadEvaluation contactDetailHeadings />

              <ContactObjectives leadId={activeLead.id} />
              <ProactiveMessages leadId={activeLead.id} />
            </div>
            <div className="w-full space-y-3">
              <div className="w-full space-y-2">
                {(crmSyncRows.length > 0
                  || activeLead.integrations?.google_ads_conversion_status
                  || activeLead.integrations?.google_ads_error) && (
                  <SectionHeader title="Integrations" icon={Plug} />
                )}
                {activeLead.integrations && (
                  <>
                    {crmSyncRows.length > 0 && (
                      <div className="mt-2 border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-[40%]">Object</TableHead>
                              <TableHead>View</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {crmSyncRows.map((row) => {
                              const hasUrl = Boolean(row.url);
                              const hasError = Boolean(row.error);
                              const crmLabel =
                                row.crm === 'salesforce'
                                  ? 'Salesforce'
                                  : row.crm === 'hubspot'
                                    ? 'HubSpot'
                                    : 'Attio';

                              return (
                                <TableRow key={`${row.crm}-${row.object}`}>
                                  <TableCell className="font-medium">{row.object}</TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span>
                                              {hasError ? (
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="border-red-500 text-red-600 hover:text-red-600"
                                                  onClick={() => {
                                                    if (row.crm === 'salesforce') {
                                                      setActiveSalesforceError(row.error || null);
                                                      setSalesforceDialogOpen(true);
                                                    } else if (row.crm === 'attio') {
                                                      setActiveAttioError(row.error || null);
                                                      setAttioDialogOpen(true);
                                                    } else {
                                                      setActiveHubspotError(row.error || null);
                                                      setHubspotDialogOpen(true);
                                                    }
                                                  }}
                                                >
                                                  <Eye className="mr-2 h-4 w-4" />
                                                  View
                                                </Button>
                                              ) : hasUrl ? (
                                                <Button variant="outline" size="sm" asChild>
                                                  <a href={row.url} target="_blank" rel="noopener noreferrer">
                                                    <Eye className="mr-2 h-4 w-4" />
                                                    View
                                                  </a>
                                                </Button>
                                              ) : (
                                                <Button variant="outline" size="sm" disabled>
                                                  <Eye className="mr-2 h-4 w-4" />
                                                  View
                                                </Button>
                                              )}
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            {hasError ? `Error: ${row.error}` : `View in ${crmLabel}`}
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                      <img src={row.icon} alt={crmLabel} className="w-5 h-5 object-contain" />
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    <Dialog
                      open={salesforceDialogOpen}
                      onOpenChange={(open) => {
                        setSalesforceDialogOpen(open);
                        if (!open) setActiveSalesforceError(null);
                      }}
                    >
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Salesforce Sync Error</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                          <div className="text-red-500 mb-4 p-3 bg-red-50 rounded-md border border-red-200">
                            {activeSalesforceError || activeLead.integrations?.salesforce_error}
                          </div>
                          <p className="text-sm text-gray-500">
                            There was an error syncing this lead to Salesforce. You can attempt to resync after fixing the issue.
                          </p>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setSalesforceDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => handleResyncSalesforce(activeLead.id)} disabled={resyncingSalesforce}>
                            {resyncingSalesforce ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Resyncing...
                              </>
                            ) : (
                              'Resync to Salesforce'
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Dialog
                      open={attioDialogOpen}
                      onOpenChange={(open) => {
                        setAttioDialogOpen(open);
                        if (!open) setActiveAttioError(null);
                      }}
                    >
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Attio Sync Error</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                          <div className="text-red-500 mb-4 p-3 bg-red-50 rounded-md border border-red-200">
                            {activeAttioError || activeLead.integrations?.attio_error || activeLead.integrations?.attio_company_error}
                          </div>
                          <p className="text-sm text-gray-500">
                            There was an error syncing this lead to Attio. You can attempt to resync after fixing the issue.
                          </p>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setAttioDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => handleResyncAttio(activeLead.id)} disabled={resyncingAttio}>
                            {resyncingAttio ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Resyncing...
                              </>
                            ) : (
                              'Resync to Attio'
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Dialog
                      open={hubspotDialogOpen}
                      onOpenChange={(open) => {
                        setHubspotDialogOpen(open);
                        if (!open) setActiveHubspotError(null);
                      }}
                    >
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>HubSpot Sync Error</DialogTitle>
                        </DialogHeader>
                        <div className="py-4">
                          <div className="text-red-500 mb-4 p-3 bg-red-50 rounded-md border border-red-200">
                            {getCustomerFacingHubspotError(activeHubspotError || activeLead.integrations?.hubspot_error)}
                          </div>
                          <p className="text-sm text-gray-500">
                            There was an error syncing this lead to HubSpot. You can attempt to resync after fixing the issue.
                          </p>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setHubspotDialogOpen(false)}>
                            Cancel
                          </Button>
                          <Button onClick={() => handleResyncHubspot(activeLead.id)} disabled={resyncingHubspot}>
                            {resyncingHubspot ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Resyncing...
                              </>
                            ) : (
                              'Resync to HubSpot'
                            )}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    {(activeLead.integrations.google_ads_conversion_status || activeLead.integrations.google_ads_error) && (
                      <>
                        {(activeLead.integrations.google_ads_conversion_status === "failed" || activeLead.integrations.google_ads_error) ? (
                          <>
                            <Dialog open={googleAdsDialogOpen} onOpenChange={setGoogleAdsDialogOpen}>
                              <DialogTrigger asChild>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div
                                        className="rounded-md p-1 border border-red-500 cursor-pointer"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setGoogleAdsDialogOpen(true);
                                        }}
                                      >
                                        <img
                                          src={GoogleAdsIcon}
                                          alt="Google Ads"
                                          className="w-10 h-10 object-contain"
                                        />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Error: Click to view details and resync</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Google Ads Conversion Tracking Error</DialogTitle>
                                </DialogHeader>
                                <div className="py-4">
                                  <div className="text-red-500 mb-4 p-3 bg-red-50 rounded-md border border-red-200">
                                    {activeLead.integrations.google_ads_conversion_error || activeLead.integrations.google_ads_error}
                                  </div>
                                  <p className="text-sm text-gray-500">
                                    There was an error tracking this lead conversion to Google Ads. You can attempt to resync after fixing the issue. Check your Google Ads integration settings and ensure you have proper permissions.
                                  </p>
                                </div>
                                <DialogFooter>
                                  <Button
                                    variant="outline"
                                    onClick={() => setGoogleAdsDialogOpen(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    onClick={() => handleResyncGoogleAds(activeLead.id)}
                                    disabled={resyncingGoogleAds}
                                  >
                                    {resyncingGoogleAds ? (
                                      <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Resyncing...
                                      </>
                                    ) : (
                                      'Resync to Google Ads'
                                    )}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div
                                  className="rounded-md p-1 border border-green-500 cursor-default"
                                >
                                  <img
                                    src={GoogleAdsIcon}
                                    alt="Google Ads"
                                    className="w-10 h-10 object-contain"
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                {activeLead.integrations.google_ads_conversion_status === "success" ? (
                                  <p className="text-green-600 dark:text-green-400">Conversion Tracked Successfully</p>
                                ) : (
                                  <p>Status Unknown</p>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="space-y-5">
              {/* Visitor Journey Section */}
              <VisitorJourney leadId={activeLead.id} contactDetail />

              <Assessments leadId={activeLead.id} contactDetail />
              <Reports leadId={activeLead.id} contactDetail />
              <DealsTable lead={activeLead} />
            </div>
          </div>
        )}
        {!activeLead && loadingLead && (
          <div className="py-12 flex items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading contact...
          </div>
        )}
        {activeLead && (
          <div className="flex space-x-2 mt-4">
            <ShinyButton
              className='flex-1'
              onClick={() => {
                onClose();
                navigate(`/sessions/?lead_id=${activeLead.id}`);
              }}
            >
              <div className='flex items-center justify-center'>
                View Sessions <ArrowRight className='w-4 h-4 ml-2' />
              </div>
            </ShinyButton>
            <ShinyButton
              className='flex-1'
              onClick={() => {
                onClose();
                navigate(`/contacts/${activeLead.id}`);
              }}
            >
              <div className='flex items-center justify-center'>
                Contact Profile <ArrowRight className='w-4 h-4 ml-2' />
              </div>
            </ShinyButton>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default LeadSheet;
